import { useEffect, useState } from 'react';
import { send } from '@/shared/messages';
import type { Test } from '@/shared/types';
import { localDayString } from '@/shared/study';
import { notifyTestsChanged } from './testEvents';
import { Button } from './Button';
import { Alert } from './Alert';
import { Card } from './Card';
import { Input } from './Field';

// Shared "Tests" settings panel used by both the extension Options page and
// the webapp Settings page. Full CRUD over tests (the parent of chapters):
// create, rename inline, delete (cascades to the test's chapters), and drag
// a row by its handle to reorder. Mutations broadcast TESTS_CHANGED_EVENT so
// the sibling ChaptersManager refreshes its test dropdown.
type TestRow = Test & { chapterCount: number; dueCount: number };

export function TestsManager() {
  const [tests, setTests] = useState<TestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [titleEdits, setTitleEdits] = useState<Record<string, string>>({});
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const r = await send({ type: 'LIST_TESTS' });
      setTests(r.tests);
      setTitleEdits({});
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // After any mutation: reload our list and tell the chapters panel.
  async function reload() {
    await load();
    notifyTestsChanged();
  }

  async function create() {
    const title = newTitle.trim();
    if (!title) return;
    setCreating(true);
    try {
      await send({ type: 'CREATE_TEST', title });
      setNewTitle('');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  }

  function titleDirty(t: TestRow): boolean {
    const v = titleEdits[t.id];
    return v !== undefined && v.trim() !== '' && v !== t.title;
  }

  async function saveTitle(t: TestRow) {
    const title = titleEdits[t.id];
    if (title === undefined || title.trim() === '' || title === t.title) return;
    setBusyId(t.id);
    try {
      await send({ type: 'UPDATE_TEST', testId: t.id, patch: { title: title.trim() } });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  // Exam date is stored as ms at local noon (dodges DST/UTC day-shift); an
  // empty picker value clears it. Drives the Today view's countdown.
  async function saveExamDate(t: TestRow, value: string) {
    setBusyId(t.id);
    try {
      await send({
        type: 'UPDATE_TEST',
        testId: t.id,
        patch: { exam_date: value ? new Date(`${value}T12:00:00`).getTime() : null },
      });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function remove(t: TestRow) {
    if (
      !confirm(
        `Delete test "${t.title}" and all ${t.chapterCount} chapter(s) in it? ` +
          `This also removes their sources, summaries, and cards. This cannot be undone.`
      )
    ) {
      return;
    }
    setBusyId(t.id);
    try {
      await send({ type: 'DELETE_TEST', testId: t.id });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  // Move the test at `from` to index `to`, renumber 0..n-1, PATCH the rows
  // whose order changed. Optimistic; refetch on failure. Shared by drag
  // (desktop) and the ▲/▼ buttons (touch).
  async function reorderTo(from: number, to: number) {
    if (from < 0 || to < 0 || to >= tests.length || from === to) return;
    const prev = tests;
    const next = [...tests];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    const renumbered = next.map((t, i) => ({ ...t, order: i }));
    setTests(renumbered);

    setReordering(true);
    try {
      const writes = renumbered
        .filter((t) => prev.find((o) => o.id === t.id)?.order !== t.order)
        .map((t) => send({ type: 'UPDATE_TEST', testId: t.id, patch: { order: t.order } }));
      await Promise.all(writes);
      notifyTestsChanged();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      await load();
    } finally {
      setReordering(false);
    }
  }

  function dropOn(targetId: string) {
    const sourceId = dragId;
    setDragId(null);
    setOverId(null);
    if (!sourceId || sourceId === targetId) return;
    void reorderTo(
      tests.findIndex((t) => t.id === sourceId),
      tests.findIndex((t) => t.id === targetId)
    );
  }

  return (
    <Card className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Tests</h2>
        <div className="flex items-center gap-2">
          {reordering && <span className="text-xs text-slate-400">Saving order…</span>}
          <Button size="xs" onClick={() => void load()}>
            Refresh
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-600">
        A test is one exam's worth of material (SEI, S7, …) and owns its chapters. Reorder with the{' '}
        <span className="font-mono">▲▼</span> buttons (or drag the <span className="font-mono">⠿</span>{' '}
        handle on desktop). Deleting a test removes all of its chapters.
      </p>

      <div className="flex items-center gap-2">
        <Input
          type="text"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void create();
          }}
          placeholder="New test name (e.g. S7)"
          className="min-w-0 flex-1"
        />
        <Button
          variant="primary"
          onClick={() => void create()}
          disabled={newTitle.trim() === ''}
          loading={creating}
        >
          {creating ? 'Adding…' : 'Add test'}
        </Button>
      </div>

      {loading ? (
        <div className="text-xs text-slate-500">Loading…</div>
      ) : tests.length === 0 ? (
        <div className="rounded border border-dashed border-slate-300 p-3 text-xs text-slate-500">
          No tests yet — add one above.
        </div>
      ) : (
        <div className="divide-y divide-slate-200">
          {tests.map((t, idx) => {
            const titleVal = titleEdits[t.id] ?? t.title;
            const isDirty = titleDirty(t);
            return (
              <div
                key={t.id}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (overId !== t.id) setOverId(t.id);
                }}
                onDrop={() => dropOn(t.id)}
                className={`flex items-center gap-2 py-2 text-sm ${
                  overId === t.id && dragId && dragId !== t.id ? 'bg-blue-50' : ''
                } ${dragId === t.id ? 'opacity-50' : ''}`}
              >
                {/* Desktop drag handle; touch uses the ▲/▼ buttons instead. */}
                <span
                  draggable
                  onDragStart={() => setDragId(t.id)}
                  onDragEnd={() => {
                    setDragId(null);
                    setOverId(null);
                  }}
                  className="hidden cursor-grab select-none px-1 text-slate-400 hover:text-slate-600 active:cursor-grabbing @md:inline"
                  title="Drag to reorder"
                >
                  ⠿
                </span>
                <div className="flex shrink-0 flex-col">
                  <button
                    onClick={() => void reorderTo(idx, idx - 1)}
                    disabled={idx === 0 || reordering}
                    className="px-1 leading-none text-slate-400 hover:text-slate-700 disabled:opacity-30"
                    title="Move up"
                    type="button"
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => void reorderTo(idx, idx + 1)}
                    disabled={idx === tests.length - 1 || reordering}
                    className="px-1 leading-none text-slate-400 hover:text-slate-700 disabled:opacity-30"
                    title="Move down"
                    type="button"
                  >
                    ▼
                  </button>
                </div>
                <Input
                  type="text"
                  value={titleVal}
                  onChange={(ev) => setTitleEdits((m) => ({ ...m, [t.id]: ev.target.value }))}
                  className="min-w-0 flex-1"
                />
                <Input
                  type="date"
                  size="sm"
                  title="Exam date — drives the Today view countdown"
                  value={t.exam_date ? localDayString(t.exam_date) : ''}
                  onChange={(ev) => void saveExamDate(t, ev.target.value)}
                  disabled={busyId === t.id}
                  className="hidden w-32 shrink-0 @md:block"
                />
                <div className="w-14 shrink-0 text-right font-mono text-xs text-slate-500 @md:w-28">
                  {t.chapterCount} ch
                  {t.dueCount > 0 && <span className="ml-1 text-blue-700">·{t.dueCount}</span>}
                </div>
                <div className="flex shrink-0 justify-end gap-1 text-xs">
                  <Button
                    variant="primary"
                    size="xs"
                    className="min-h-[40px]"
                    onClick={() => void saveTitle(t)}
                    disabled={!isDirty || busyId === t.id}
                  >
                    Save
                  </Button>
                  <Button
                    variant="danger"
                    size="xs"
                    className="min-h-[40px]"
                    onClick={() => void remove(t)}
                    disabled={busyId === t.id}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {error && <Alert>{error}</Alert>}
    </Card>
  );
}
